import type { CrowdLevel, TimeSlot } from '../types';

export interface TimedOccurrence {
  time: string;       // "HH:MM" programmé
  date: string;       // "YYYY-MM-DD"
  seatsSold: number;
  seatsTotal: number;
}

export interface TimeAggregate {
  time: string;
  sampleSize: number;     // occurrences à seatsTotal > 0
  avgOccupancy: number;   // 0-1
}

export const MIN_DISTINCT_DAYS = 2;
export const LOW_CONFIDENCE_THRESHOLD = 2;

export function scoreToLevel(score: number): CrowdLevel {
  if (score < 45) return 'low';
  if (score < 70) return 'medium';
  return 'high';
}

// Hypothèse : une ligne par couple (time, date). reconstructSchedule dédoublonne les
// dates via un Set ; aggregateByTime, lui, compte les lignes — d'éventuels doublons
// exacts en amont gonfleraient sampleSize. Le nettoyage relève de l'appelant.
export function reconstructSchedule(
  occurrences: TimedOccurrence[],
  minDistinctDays = MIN_DISTINCT_DAYS,
): string[] {
  const datesByTime = new Map<string, Set<string>>();
  for (const o of occurrences) {
    const set = datesByTime.get(o.time) ?? new Set<string>();
    set.add(o.date);
    datesByTime.set(o.time, set);
  }
  return [...datesByTime.entries()]
    .filter(([, dates]) => dates.size >= minDistinctDays)
    .map(([time]) => time)
    .sort((a, b) => a.localeCompare(b));
}

export function aggregateByTime(occurrences: TimedOccurrence[]): Map<string, TimeAggregate> {
  const sums = new Map<string, { sold: number; total: number; count: number }>();
  for (const o of occurrences) {
    if (o.seatsTotal <= 0) continue;
    const e = sums.get(o.time) ?? { sold: 0, total: 0, count: 0 };
    e.sold += o.seatsSold;
    e.total += o.seatsTotal;
    e.count += 1;
    sums.set(o.time, e);
  }
  // Ratio pondéré par la capacité (Σvendues / Σtotal) : un grand car compte plus
  // qu'un minibus à moitié plein, plutôt qu'une simple moyenne des taux par départ.
  const out = new Map<string, TimeAggregate>();
  for (const [time, e] of sums) {
    out.set(time, {
      time,
      sampleSize: e.count,
      avgOccupancy: e.total > 0 ? e.sold / e.total : 0,
    });
  }
  return out;
}

export function buildPredictedSlots(
  schedule: string[],
  agg: Map<string, TimeAggregate>,
  fallbackScore: number,
): TimeSlot[] {
  return schedule.map((time) => {
    const a = agg.get(time);
    if (a && a.sampleSize >= 1) {
      const score = Math.min(100, Math.max(0, Math.round(a.avgOccupancy * 100)));
      return {
        time,
        crowd_level: scoreToLevel(score),
        crowd_score: score,
        is_recommended: false,
        data_source: 'historical' as const,
        sample_size: a.sampleSize,
        total_departures: 0,
        low_confidence: a.sampleSize < LOW_CONFIDENCE_THRESHOLD,
      };
    }
    const score = Math.min(100, Math.max(0, Math.round(fallbackScore)));
    return {
      time,
      crowd_level: scoreToLevel(score),
      crowd_score: score,
      is_recommended: false,
      data_source: 'synthetic' as const,
      sample_size: 0,
      total_departures: 0,
      low_confidence: true,
    };
  });
}
